import type { EmbedConfig } from "../../search/rules/params.js";
import type { EmbedConfigFieldKey } from "./snippet.js";

/**
 * embedFieldDependencies names the embed fields that cannot stand on their own, and
 * the field each one needs.
 *
 * Both entries depend on the elevation chart, and for the same reason: they are read
 * AGAINST it. The analysis bars are revealed by hovering the curve and are aligned to its
 * distance axis; the pace control re-times the figures the curve is drawn from. With the
 * chart switched off, one is a legend for a picture nobody can see and the other is a rate
 * field beside nothing that moves.
 *
 * The pace control needs the CHARACTERISTICS row as well, and that one is about where it
 * renders rather than what it means: the control lives in that row's chrome, beside the
 * segment chooser, because that is where this profile keeps the things a reader operates.
 * With characteristics off there is no such row for it to sit in.
 *
 * A dependency is NOT a safety rule, which is why it lives here rather than beside them.
 * A safety rule is the ORGANIZATION capping what a publisher may offer a visitor, and it
 * is enforced server-side. This is the publisher's own configuration disagreeing with
 * itself, and the wizard's job is to stop them building it rather than to refuse it later.
 */
export const embedFieldDependencies = {
  withProfileAnalysis: ["withProfileElevation"],
  withProfilePaceControl: ["withProfileElevation", "withProfileCharacteristics"],
} as const satisfies Partial<Record<EmbedConfigFieldKey, readonly EmbedConfigFieldKey[]>>;

/**
 * DependentFieldKey is an embed field that needs another one on, derived from
 * {@link embedFieldDependencies}.
 */
export type DependentFieldKey = keyof typeof embedFieldDependencies;

/** isDependentFieldKey narrows a field key to one {@link embedFieldDependencies} names. */
const isDependentFieldKey = (key: string): key is DependentFieldKey =>
  Object.hasOwn(embedFieldDependencies, key);

/**
 * fieldDependenciesUnmet answers which fields this one is waiting on, empty when it is
 * free to be set.
 *
 * It returns the BLOCKING KEYS rather than a boolean so a surface can name them: "turn the
 * elevation chart on" is an instruction a publisher can act on, where a greyed-out switch
 * with no reason is a bug report. ALL of them rather than the first, because a control can
 * be waiting on two and naming one sends the publisher back for a second round.
 *
 * @param key The field a control sets.
 * @param config The config the control is part of.
 * @returns Every field `key` needs that `config` has off; empty for a field that needs none.
 */
export const fieldDependenciesUnmet = (
  key: EmbedConfigFieldKey,
  config: EmbedConfig,
): readonly EmbedConfigFieldKey[] => {
  if (!isDependentFieldKey(key)) {
    return [];
  }

  const required: readonly EmbedConfigFieldKey[] = embedFieldDependencies[key];

  return required.filter((field) => config[field] === false);
};

/**
 * clampConfigToFieldDependencies forces every dependent field off while the field it
 * needs is off.
 *
 * The clamp runs on the CONFIG rather than only on the controls, and that is what keeps
 * the wizard honest: the snippet it emits, the preview it renders and the switches it
 * shows are one state. Disabling a switch and leaving the value true would emit an embed
 * whose analysis is on and whose chart is off, which is the combination this exists to
 * prevent. A live embed runs it too, through {@link clampEmbedConfigToSecurity}.
 *
 * Turning the chart back on does NOT restore them. They are unchecked, and an unchecked
 * box that silently rechecks itself is a worse surprise than one the publisher sets again.
 */
export const clampConfigToFieldDependencies = (config: EmbedConfig): EmbedConfig => {
  let next = config;
  for (const key of Object.keys(embedFieldDependencies).filter(isDependentFieldKey)) {
    if (next[key] && fieldDependenciesUnmet(key, next).length > 0) {
      next = { ...next, [key]: false };
    }
  }

  return next;
};
